/**
 * Edit Workspace Dialog
 *
 * Simplified dialog for creating a new workspace (name only).
 * After creation, redirects to the full workspace edit page.
 * Editing is handled by the WorkspaceEdit page.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '../ui';
import { workspacesClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';

interface EditWorkspaceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditWorkspaceDialog({
  open,
  onClose,
  onSuccess,
}: EditWorkspaceDialogProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name?.trim()) {
      setError('Workspace name is required');
      return;
    }

    try {
      setLoading(true);
      const created = await workspacesClient.createWorkspace({ name });
      alert(enqueueSnackbar, 'Workspace created successfully', 'success');
      onSuccess?.();
      onClose();
      navigate(`/workspaces/${created.id}`);
    } catch (error: any) {
      const errorMessage = error?.response?.data;
      alert(enqueueSnackbar, typeof errorMessage === 'string' ? errorMessage : 'Failed to create workspace', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Workspace</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Workspace Name"
            fullWidth
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            error={!!error}
            helperText={error}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
